import React, { useContext, useEffect, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { space, position, flexbox, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import selectProfile from 'redux-api/reselect/profile';
import selectTeams from 'redux-api/reselect/teams';
import Btn from 'components/btn-menu';
import { SymbolLogo } from 'theme/logo';
import Dropdown from 'theme/dropdown';
import DropdownItem from 'theme/dropdown/item';
import Burger from 'theme/icons/burger';
import Close from 'theme/icons/close';
import Feed from 'theme/icons/feed';
import Timelinechart from 'theme/icons/timelinechart';
import Card from 'theme/icons/card';
import Help from 'theme/icons/help';
import Lightning from 'theme/icons/lightning';
import ListUl from 'theme/icons/list-ul';
import BtnLogout from 'components/btn-logout';
import Text from 'theme/text';
import OnboardingChart from 'theme/onboarding';
import translations from 'translations';

const Wrapper = styled.div`
  ${position}
  ${layout}

  height: 100vh;
  position: fixed;
  left: 0;
  display: flex;
  justify-content: center;

  z-index: 999;

  /* On a phone the rail is a drawer: off screen until the burger opens it, so
     the page keeps its whole width. From the first breakpoint up it is the
     fixed rail it has always been, and the button below is not rendered. */
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
  transition: transform 0.2s ease-in-out;

  @media (min-width: ${themeGet('breakpoints.0')}) {
    transform: none;
  }
`;

/* Closed, the drawer takes the logo off screen with it, so the phone gets its
   own small mark beside the button — otherwise the top of the page says
   nothing about whose application this is. */
const MobileBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 56px;
  z-index: 1001;

  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;

  @media (min-width: ${themeGet('breakpoints.0')}) {
    display: none;
  }
`;

const BurgerButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: ${themeGet('colors.primary_500')};
  color: ${themeGet('colors.white')};
`;

/* Inside the drawer and at its far corner: over the logo it read as a cross on
   the mark itself, which is where a person looks first. */
const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;

  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  color: ${themeGet('colors.white')};

  @media (min-width: ${themeGet('breakpoints.0')}) {
    display: none;
  }
`;

/* Tapping anything else closes the drawer, which is what a phone user expects
   and what stops the page underneath being operated blind. */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
  background: rgba(0, 0, 0, 0.4);

  @media (min-width: ${themeGet('breakpoints.0')}) {
    display: none;
  }
`;

const Content = styled.div`
  ${space}

  overflow: auto;

  background: ${themeGet('colors.primary_500')};

  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &:-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
`;

const Box = styled.div`
  ${flexbox}
  ${layout}
  ${space}

  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WrapHelp = styled.div`
  ${space}

  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${themeGet('colors.white')};
`;

const WrapUpgrade = styled.div`
  ${space}
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${themeGet('colors.white')};
`;

const WrapItem = styled.a`
  width: 100%;
  height: 100%;
  text-decoration: none;
  white-space: nowrap;
`;

const StyledA = styled.a`
  width: 100%;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
`;

function Menu() {
  const { pathname } = useRouter();

  const theme = useContext(ThemeContext);
  const { languageCode } = useSelector((state) => state.config);
  const {
    completitionPerc,
    payload: { company, onboard, hasPROPlan },
  } = useSelector(selectProfile);
  const { canAccessAnalytics, canAccessBilling } = useSelector(selectTeams);

  const [isOpen, setIsOpen] = useState(false);

  // A tap that leads somewhere has already done its job: the drawer closes with
  // the page change rather than staying over what the person went to read.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);
  
  const {
    completition: { current } = { current: 0 },
  } = onboard || {};

  return (
    <>
      {!isOpen && (
        <MobileBar>
          <BurgerButton
            type="button"
            aria-label="Open menu"
            aria-expanded={false}
            onClick={() => setIsOpen(true)}
          >
            <Burger />
          </BurgerButton>
          <SymbolLogo
            style={{
              color: themeGet('colors.primary_500')({ theme }),
              display: 'block',
              height: ['32px'],
            }}
          />
        </MobileBar>
      )}

      {isOpen && <Backdrop onClick={() => setIsOpen(false)} />}

      <Wrapper width={['200px', '96px', '96px', '165px']} top="0" bottom="0" isOpen={isOpen}>
        <CloseButton type="button" aria-label="Close menu" onClick={() => setIsOpen(false)}>
          <Close />
        </CloseButton>
      <Content pt={['s', 's', 's', 'l']}>
        <Box flexDirection="column">
          {company?.code === 'SL_DE' ? (
            <img src="/images/bookrix_logo.png" width="60" height="60" alt="Logo BookRix" />
          ) : (
            <SymbolLogo
              style={{
                color: themeGet('colors.white')({ theme }),
                display: 'block',
                height: ['48px', '42px', '42px', '64px'],
                mb: ['m', 'xs', 'xs', '0'],
              }}
            />
          )}
          <Btn
            title="dashboard"
            style={{
              mt: 'l',
            }}
            isFocused
            route={['/', '/dashboard', '/home']}
          >
            <Feed />
            <Text
              fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
              lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
              fontWeight="normal"
              color="inherit"
            >
              {translations[languageCode].dashboard}
            </Text>
          </Btn>
          <Btn title="services" route={['/services']}>
            <ListUl />
            <Text
              fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
              lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
              fontWeight="normal"
              color="inherit"
            >
              {translations[languageCode].Services}
            </Text>
          </Btn>
          {canAccessAnalytics && (
            <Btn title="analytics" route={['/analytics']}>
              <Timelinechart />
              <Text
                fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                fontWeight="normal"
                color="inherit"
              >
                {translations[languageCode].analytics}
              </Text>
            </Btn>
          )}
          {canAccessBilling && (
            <Btn title="billing" route={['/billing']}>
              <Card />
              <Text
                fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                fontWeight="normal"
                color="inherit"
              >
                {translations[languageCode].billing}
              </Text>
            </Btn>
          )}
        </Box>
        <Box flexDirection="column" mt="m">
          {company && company.code === 'SL_US' && !hasPROPlan && (
            <a href="https://www.streetlib.com/pricing/" target="_blank" rel="noopener noreferrer">
              <WrapUpgrade>
                <Lightning color="#EF9800" />
                <Text
                  fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                  lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                  fontWeight="normal"
                  color="inherit"
                >
                  {translations[languageCode].upgrade}
                </Text>
              </WrapUpgrade>
            </a>
          )}

          <Dropdown
            placement="topRight"
            options={[
              <WrapItem
                title={translations[languageCode].faq}
                href="https://help.streetlib.com"
                key="help"
              >
                <DropdownItem label={translations[languageCode].faq} />
              </WrapItem>,
              <WrapItem
                title={translations[languageCode].community}
                href="https://community.streetlib.it/join?invitation_token=fb074f6465dee2de3ec0d2d29446de14ed7ccc87-e595924a-07b0-44bc-8601-c10dfd7e59dd"
                key="community"
              >
                <DropdownItem label={translations[languageCode].community} />
              </WrapItem>,
              <WrapItem
                title={translations[languageCode]['privacy policy']}
                href="https://www.streetlib.com/privacy"
                key="privacy"
              >
                <DropdownItem label={translations[languageCode]['privacy policy']} />
              </WrapItem>,
            ]}
          >
            <WrapHelp mb="s">
              <Help />
              <Text
                fontSize={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                lineHeights={['pSmall', 'pSmall', 'pSmall', 'pLarge']}
                fontWeight="normal"
                color="inherit"
              >
                {translations[languageCode].help}
              </Text>
            </WrapHelp>
          </Dropdown>

          <BtnLogout />

          {current !== 100 && (
            <Link href="/onboarding">
              <StyledA>
                <OnboardingChart
                  disabled={pathname === '/onboarding'}
                  percent={completitionPerc}
                  label={translations[languageCode]['On Boarding']}
                />
              </StyledA>
            </Link>
          )}
        </Box>
        </Content>
      </Wrapper>
    </>
  );
}

export default Menu;
